import { observable, action, runInAction } from "mobx";
import { success, error, info } from "../../../../utils/MensagemUtil";
import { isNuloOuVazio, validarCamposObrigatorios } from "../../../../utils/Validate";
import { Composicao, Grupo, Insumo } from "../../../../services";
import * as util from "../../../../utils/MascaraUtil";

class ComposicaoStore {
  @observable
  composicao = {
    id: null,
    nome: "",
    tipoUnidade: ""
  };

  @observable
  valorTotalCustoMaterial = 0;

  @observable
  valorTotalCustoMaoObra = 0;

  @observable
  valorTotalGeral = 0;

  @observable
  modal = false;

  @observable
  grupos = [];

  @observable
  item = {
    nome: "",
    valor: "",
    quantidade: 0,
    insumo: {
      id: null,
      nome: "-",
      grupo: "-",
      valor: 0,
      valorTotal: 0,
      percentual: 0
    }
  };

  // @observable
  // insumo = "";

  @observable
  insumos = [];

  @observable
  itens = [];

  @observable
  criterio = {
    nome: "",
    grupo: ""
  };

  @observable
  composicoes = [];

  @observable
  paginacao = {};

  @observable
  fetching = false;

  @action.bound
  reset = () => {
    this.composicao = {
      nome: "",
      tipoUnidade: ""
    };
    this.valorTotalCustoMaterial = 0;
    this.valorTotalCustoMaoObra = 0;
    this.valorTotalGeral = 0;
    this.itens = [];
  };

  @action.bound
  async filtrar() {
    try {
      this.fetching = true;

      const result = await Composicao.consultar(this.criterio);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.composicoes = result.data;
      this.paginacao = {
        ...result.paging,
        pagina: this.criterio.pagina || 1
      };

      if (!isNuloOuVazio(this.composicoes)) {
        this.calcularTotaisComposicoes();
      }
    } finally {
      this.fetching = false;
    }
  }

  calcularTotaisComposicoes = () => {
    this.composicoes = this.composicoes.map(c => ({
      ...c,
      valorTotal: c.valorTotalMaterial + c.valorTotalMaoObra
    }));
  };

  @action.bound
  async excluir(id) {
    try {
      this.fetching = true;

      const result = Composicao.excluirComposicao(id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      success("Composição excluída com sucesso!");
      this.filtrar();
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  paginar = target => {
    this.criterio = {
      ...this.criterio,
      pagina: target.page,
      limite: target.limit
    };

    this.filtrar();
  };

  @action.bound
  handleChangeFiltro = ({ target }) => {
    const { name, value } = target;

    this.criterio[name] = value;
  };

  @action.bound
  async carregarGrupos() {
    try {
      this.fetching = true;

      const result = await Grupo.consultar({ limite: 100 });
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.grupos = result.data;
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async salvar(limpar = false) {
    try {
      this.fetching = true;

      const erros = this.validarComposicaoOrcamento(this.composicao);
      if (erros.length > 0) {
        error(erros);
        return;
      }

      const result = await Composicao.cadastrarComposicao(this.toComposicao());
      if (result.erro) {
        error(result.erro);
        return;
      }

      success("Composição cadastrada com sucesso!");

      limpar && this.reset();
    } finally {
      this.fetching = false;
    }
  }

  validarComposicaoOrcamento = registro => {
    const erros = validarCamposObrigatorios()
      .addSeNuloOuVazio("Nome", registro.nome)
      .addSeNuloOuVazio("Unidade/Tipo", registro.tipoUnidade)
      .addSeNuloOuVazio("Itens da composição", this.itens.slice())
      .build();

    return erros;
  };

  @action.bound
  handleChange = ({ target }) => {
    const { name, value } = target;

    this.composicao[name] = value;
  };

  @action.bound
  iniciarIncluirItens = () => {
    this.resetItem();
    this.toggle();
  };

  resetItem() {
    this.item = {
      nome: "",
      valor: "",
      quantidade: 0,
      insumo: {
        id: null,
        nome: "-",
        grupo: "-",
        valor: 0,
        valorTotal: 0,
        percentual: 0
      }
    };
  }

  @action.bound
  toggle = () => {
    this.modal = !this.modal;
  };

  @action.bound
  selecionarInsumo = insumo => {
    this.item = {
      insumo,
      quantidade: 0,
      nome: insumo.nome,
      valor: insumo.valorTotal,
      selecao: insumo.id
    };
  };

  @action.bound
  handleChangeItem = ({ target }) => {
    const { name, value } = target;

    this.item[name] = value;
  };

  @action.bound
  incluirItem = () => {
    const erros = this.validarCadastroItem(this.item);
    if (erros.length > 0) {
      error(erros);
      return;
    }

    const valor = util.remover(this.item.valor);
    const quantidade = util.remover(this.item.quantidade);
    const item = {
      ...this.item,
      subgrupoId: this.item.insumo.id,
      valor: valor,
      quantidade: quantidade,
      tipoUnidade: this.item.insumo.unidade,
      isMaoDeObra: this.item.insumo.isMaoDeObra,
      valorTotal: parseFloat((valor * quantidade).toFixed(2))
    };

    this.itens.push(item);

    this.calcularTotaisItens();

    info(`${this.item.insumo.nome} incluído!`);
    this.resetItem();
  };

  @action.bound
  removerItem = item => {
    this.itens.remove(item);

    this.calcularTotaisItens();
  };

  validarCadastroItem = item => {
    const erros = validarCamposObrigatorios()
      .addSeMenorIgualZero("Insumo", item.insumo.id)
      .addSeNuloOuVazio("Nome do item", item.nome)
      .addSeNuloOuVazio("Valor do item R$", item.valor)
      .addSeMenorIgualZero("Quantidade", item.quantidade)
      .build();

    return erros;
  };

  @action.bound
  calcularTotaisItens = () => {
    runInAction(() => {
      this.valorTotalCustoMaterial = this.obterTotalCustoMaterial();
      this.valorTotalCustoMaoObra = this.obterTotalCustoMaoObra();
      this.valorTotalGeral = this.valorTotalCustoMaterial + this.valorTotalCustoMaoObra;
    });
  };

  obterTotalCustoMaterial = () => {
    let total = 0;
    this.itens.forEach(item => {
      if (!item.isMaoDeObra) {
        total += parseFloat(item.valorTotal, 0);
      }
    });

    return total;
  };

  obterTotalCustoMaoObra = () => {
    let total = 0;
    this.itens.forEach(item => {
      if (item.isMaoDeObra) {
        total += parseFloat(item.valorTotal, 0);
      }
    });

    return total;
  };

  @action.bound
  async filtrarInsumos() {
    try {
      this.fetching = true;

      const result = await Insumo.consultar(this.criterio);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.insumos = result.data.map(insumo => ({
        ...insumo,
        valorTotal: insumo.valor - (insumo.valor * insumo.percentual) / 100
      }));
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async obter(id) {
    try {
      this.fetching = true;

      const result = await Composicao.buscarComposicao(id);
      if (result && result.erro) {
        error(result.erro);
        return;
      }

      this.composicao = result;

      if (result.itens) {
        this.itens = result.itens.map(item => ({
          ...item,
          valorTotal: parseFloat((item.valor * item.quantidade).toFixed(2))
        }));
      }

      this.calcularTotaisItens();
    } finally {
      this.fetching = false;
    }
  }

  @action.bound
  async alterar(history) {
    try {
      this.fetching = true;

      const erros = this.validarComposicaoOrcamento(this.composicao);
      if (erros.length > 0) {
        error(erros);
        return;
      }

      const result = await Composicao.alterarComposicao(this.toComposicao());
      if (result.erro) {
        error(result.erro);
        return;
      }

      success("Composição alterada com sucesso!");
      history.push(`/cadastros/composicoes/`);
    } finally {
      this.fetching = false;
    }
  }

  validarComposicaoOrcamento = registro => {
    const erros = validarCamposObrigatorios()
      .addSeNuloOuVazio("Nome", registro.nome)
      .addSeNuloOuVazio("Unidade/Tipo", registro.tipoUnidade)
      .addSeNuloOuVazio("Itens da composição", this.itens.slice())
      .build();

    return erros;
  };

  toComposicao() {
    const novo = {
      id: this.composicao.id,
      nome: this.composicao.nome,
      tipoUnidade: parseInt(this.composicao.tipoUnidade, 0),
      itens: this.itens,
      valorTotalMaterial: this.valorTotalCustoMaterial,
      valorTotalMaoObra: this.valorTotalCustoMaoObra
    };

    return novo;
  }
}

export default new ComposicaoStore();



// WEBPACK FOOTER //
// ./src/views/cadastros/composicoes/stores/ComposicaoStore.js